import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { ActivitySlot } from '../../../_models/schedule.model';
import './SlotEditPanel.css';

interface SlotEditPanelProps {
  open: boolean;
  slot: ActivitySlot | null;
  timetableId: number;
  onClose: () => void;
  onSave: (slotId: number, data: Partial<ActivitySlot>) => Promise<void>;
  onDelete: (slotId: number) => Promise<void>;
  onAdd?: (data: Partial<ActivitySlot>) => Promise<void>;
}

const SlotEditPanel: React.FC<SlotEditPanelProps> = ({
  open,
  slot,
  timetableId,
  onClose,
  onSave,
  onDelete,
  onAdd,
}) => {
  const [formData, setFormData] = useState<Partial<ActivitySlot>>({
    date: '',
    startTime: '',
    endTime: '',
    instructorId: 0,
    instructorName: '',
    roomId: 0,
    roomName: '',
    slotStatus: 'Scheduled',
    remarks: '',
    name: '',
    topicName: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Populate form when slot changes
  useEffect(() => {
    if (slot) {
      console.log('SlotEditPanel - slot.date:', slot.date, 'type:', typeof slot.date);

      // Format date to YYYY-MM-DD for the date input
      let formattedDate = '';
      if (slot.date) {
        try {
          // Convert to string and handle different date formats
          const dateStr = String(slot.date);
          // Extract just the date part (YYYY-MM-DD)
          if (dateStr.includes('T')) {
            formattedDate = dateStr.split('T')[0];
          } else if (dateStr.includes(' ')) {
            formattedDate = dateStr.split(' ')[0];
          } else {
            // Assume it's already in YYYY-MM-DD format
            formattedDate = dateStr;
          }
          console.log('SlotEditPanel - formattedDate:', formattedDate);
        } catch (error) {
          console.error('Error formatting date:', error);
          formattedDate = '';
        }
      }

      setFormData({
        date: formattedDate,
        startTime: slot.startTime || '',
        endTime: slot.endTime || '',
        instructorId: slot.instructorId || 0,
        instructorName: slot.instructorName || '',
        roomId: slot.roomId || 0,
        roomName: slot.roomName || '',
        slotStatus: slot.slotStatus || 'Scheduled',
        remarks: slot.remarks || '',
        name: slot.name || '',
        topicName: slot.topicName || '',
      });
    } else {
      // Reset for new slot
      setFormData({
        date: '',
        startTime: '',
        endTime: '',
        instructorId: 0,
        instructorName: '',
        roomId: 0,
        roomName: '',
        slotStatus: 'Scheduled',
        remarks: '',
        name: '',
        topicName: '',
      });
    }
  }, [slot]);

  const handleInputChange = (field: keyof ActivitySlot, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    if (!slot && !onAdd) return;

    setSaving(true);
    try {
      if (slot) {
        await onSave(slot.id, formData);
      } else if (onAdd) {
        await onAdd(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving slot:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!slot) return;

    setDeleting(true);
    try {
      await onDelete(slot.id);
      setDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      console.error('Error deleting slot:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <div className={`slot-edit-panel ${open ? 'open' : ''}`}>
        <div className="slot-edit-panel-overlay" onClick={onClose}></div>
        <div className="slot-edit-panel-content">
          {/* Header */}
          <div className="slot-edit-panel-header">
            <div>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {slot ? 'Edit Activity Slot' : 'Add Activity Slot'}
              </Typography>
              {slot && (
                <Typography variant="caption" color="textSecondary">
                  Slot ID: {slot.id}
                </Typography>
              )}
            </div>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <Divider />

          {/* Form */}
          <div className="slot-edit-panel-body">
            {/* Topic/Name */}
            <TextField
              fullWidth
              label="Topic / Name"
              value={formData.topicName || formData.name || ''}
              onChange={(e) => handleInputChange('topicName', e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
            />

            {/* Date */}
            <TextField
              fullWidth
              type="date"
              label="Date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            {/* Time Range */}
            <Box display="flex" style={{ gap: '16px' }}>
              <TextField
                fullWidth
                type="time"
                label="Start Time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                margin="normal"
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 1800 }}
              />
              <TextField
                fullWidth
                type="time"
                label="End Time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                margin="normal"
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 1800 }}
              />
            </Box>

            {/* Instructor (Read-only for now - would need instructor list) */}
            <TextField
              fullWidth
              label="Instructor"
              value={formData.instructorName || 'Not Assigned'}
              margin="normal"
              variant="outlined"
              size="small"
              disabled
              helperText="Use Advanced Editor to change instructor"
            />

            {/* Room (Read-only for now - would need room list) */}
            <TextField
              fullWidth
              label="Room"
              value={formData.roomName || 'Not Assigned'}
              margin="normal"
              variant="outlined"
              size="small"
              disabled
              helperText="Use Advanced Editor to change room"
            />

            {/* Status */}
            <FormControl fullWidth margin="normal" variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.slotStatus || 'Scheduled'}
                onChange={(e) => handleInputChange('slotStatus', e.target.value)}
                label="Status"
                MenuProps={{
                  PaperProps: {
                    style: { zIndex: 10001 },
                  },
                  PopoverClasses: {
                    root: 'slot-status-menu',
                  },
                }}
              >
                <MenuItem value="Pre-Scheduled">Pre-Scheduled</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
                <MenuItem value="Conducted">Conducted</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            {/* Remarks */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks"
              value={formData.remarks || ''}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              margin="normal"
              variant="outlined"
              size="small"
              placeholder="Add any notes or comments..."
            />

            {/* Slot Info (if editing) */}
            {slot && (
              <Box mt={2} p={2} style={{ background: 'var(--surface-page)', borderRadius: 8 }}>
                <Typography variant="caption" style={{ fontWeight: 600, color: 'var(--text-muted)' }}>
                  ADDITIONAL INFO
                </Typography>
                {slot.shiftName && (
                  <Typography variant="body2" style={{ marginTop: 4 }}>
                    <strong>Shift:</strong> {slot.shiftName}
                  </Typography>
                )}
                {slot.centreName && (
                  <Typography variant="body2" style={{ marginTop: 4 }}>
                    <strong>Centre:</strong> {slot.centreName}
                  </Typography>
                )}
                {slot.batches && slot.batches.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="body2" style={{ marginBottom: 4 }}>
                      <strong>Cohorts:</strong>
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: '8px' }}>
                      {slot.batches.map((batch) => (
                        <Chip
                          key={batch.id}
                          label={batch.batchName || `Cohort ${batch.batchId}`}
                          size="small"
                          style={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </div>

          {/* Footer Actions */}
          <div className="slot-edit-panel-footer">
            {slot && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={handleDeleteClick}
                disabled={saving || deleting}
                style={{ marginRight: 'auto' }}
              >
                Delete
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={saving || deleting}
              style={{ marginLeft: slot ? 0 : 'auto' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || deleting}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Activity Slot</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this activity slot? This action cannot be undone.
          </DialogContentText>
          {slot && (
            <Box mt={2} p={2} style={{ background: '#fff5f5', borderRadius: 4 }}>
              <Typography variant="body2">
                <strong>{slot.topicName || slot.name || 'Untitled Slot'}</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatDate(slot.date)} • {slot.startTime} - {slot.endTime}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="default" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="secondary"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SlotEditPanel;
